import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Target, Plus, Pencil, Trash2, X } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import WhsGate from '../../../components/whs/WhsGate';
import Button from '../../../components/ui/Button';
import whsService from '../../../services/whs';
import userService from '../../../services/user';
import type { UserData } from '../../../services/user';
import type { Objective, ObjectiveStatus } from '../../../types/whs';
import { Badge, OBJECTIVE_STATUS_LABEL, OBJECTIVE_STATUS_CLASS } from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';
import { toNum } from '@/utils/numeric';

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';
const labelCls = 'block text-xs font-medium text-neutral-600 mb-1';

const orNull = (v: string) => (v.trim() === '' ? null : v.trim());
const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v));
const userLabel = (u: UserData) => [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username;

const METRIC_SUGGESTIONS = ['LTIFR', 'TRIFR', 'near_miss_count', 'actions_closed_pct'];

function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
          <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

interface ObjForm {
  title: string;
  description: string;
  metric: string;
  target_value: string;
  current_value: string;
  unit: string;
  period: string;
  due_date: string;
  responsible_id: string;
  status: ObjectiveStatus;
}

const emptyForm: ObjForm = {
  title: '', description: '', metric: '', target_value: '', current_value: '',
  unit: '', period: '', due_date: '', responsible_id: '', status: 'on_track',
};

function ObjectivesInner() {
  const orgTz = useOrgTimezone();

  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Objective | null>(null);
  const [form, setForm] = useState<ObjForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await whsService.listObjectives();
    if (res.success) { setObjectives(res.data); setError(null); } else setError(res.error.message);
    setLoading(false);
  }, []);

  const loadUsers = useCallback(async () => {
    const res = await userService.getUsers();
    if (res.success) setUsers(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (o: Objective) => {
    setEditing(o);
    setForm({
      title: o.title,
      description: o.description ?? '',
      metric: o.metric ?? '',
      target_value: o.target_value != null ? String(o.target_value) : '',
      current_value: o.current_value != null ? String(o.current_value) : '',
      unit: o.unit ?? '',
      period: o.period ?? '',
      due_date: o.due_date ?? '',
      responsible_id: o.responsible_id != null ? String(o.responsible_id) : '',
      status: o.status,
    });
    setModalOpen(true);
  };

  const submit = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    const payload: Partial<Objective> = {
      title: form.title.trim(),
      description: orNull(form.description),
      metric: orNull(form.metric),
      target_value: numOrNull(form.target_value),
      current_value: numOrNull(form.current_value),
      unit: orNull(form.unit),
      period: orNull(form.period),
      due_date: orNull(form.due_date),
      responsible_id: form.responsible_id ? Number(form.responsible_id) : null,
      status: form.status,
    };
    const res = editing
      ? await whsService.updateObjective(editing.id, payload)
      : await whsService.createObjective(payload);
    setSaving(false);
    if (res.success) { setModalOpen(false); setError(null); await load(); }
    else setError(res.error.message);
  };

  const remove = async (o: Objective) => {
    if (!window.confirm(`Delete objective "${o.title}"?`)) return;
    const res = await whsService.deleteObjective(o.id);
    if (res.success) await load(); else setError(res.error.message);
  };

  const progress = (o: Objective): number | null => {
    const target = toNum(o.target_value);
    const current = toNum(o.current_value);
    if (target == null || current == null || target === 0) return null;
    return Math.max(0, Math.min(100, (current / target) * 100));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Objectives & KPIs"
        subtitle="Safety objectives and targets (LTIFR, TRIFR, near-miss count, action close-out)"
        icon={<Target className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        <div className="mb-6 flex justify-end">
          <Button onClick={openCreate}><Plus className="h-4 w-4" /> New objective</Button>
        </div>

        <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-4 py-3">Objective</th>
                <th className="px-4 py-3">Metric</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3">Responsible</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-neutral-400">Loading…</td></tr>
              ) : objectives.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-neutral-400">No objectives yet.</td></tr>
              ) : objectives.map((o) => {
                const pct = progress(o);
                return (
                  <tr key={o.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-3 font-medium text-neutral-900">{o.title}</td>
                    <td className="px-4 py-3 text-neutral-600">{o.metric || '—'}</td>
                    <td className="px-4 py-3">
                      {pct != null ? (
                        <div className="w-40">
                          <div className="mb-1 flex justify-between text-xs text-neutral-500">
                            <span>{o.current_value}{o.unit ? ` ${o.unit}` : ''}</span>
                            <span>/ {o.target_value}{o.unit ? ` ${o.unit}` : ''}</span>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-200">
                            <div className="h-full rounded-full bg-primary-600" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      ) : (
                        <span className="text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-600">{o.period || '—'}</td>
                    <td className="px-4 py-3 text-neutral-600">{o.due_date ? formatLocalDateInTz(o.due_date, orgTz) : '—'}</td>
                    <td className="px-4 py-3 text-neutral-600">{[o.responsible_first_name, o.responsible_last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td className="px-4 py-3"><Badge label={OBJECTIVE_STATUS_LABEL[o.status]} className={OBJECTIVE_STATUS_CLASS[o.status]} /></td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(o)} className="rounded p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => remove(o)} className="rounded p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <Modal
          title={editing ? 'Edit objective' : 'New objective'}
          onClose={() => setModalOpen(false)}
          footer={(
            <>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={submit} isLoading={saving}>{editing ? 'Save changes' : 'Create'}</Button>
            </>
          )}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={labelCls}>Title *</label>
              <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Description</label>
              <textarea rows={2} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Metric</label>
              <input className={inputCls} list="metric-suggestions" value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} />
              <datalist id="metric-suggestions">
                {METRIC_SUGGESTIONS.map((m) => <option key={m} value={m} />)}
              </datalist>
            </div>
            <div>
              <label className={labelCls}>Unit</label>
              <input className={inputCls} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Target value</label>
              <input type="number" className={inputCls} value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Current value</label>
              <input type="number" className={inputCls} value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Period</label>
              <input className={inputCls} placeholder="e.g. FY2026" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Due date</label>
              <input type="date" className={inputCls} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Responsible person</label>
              <select className={inputCls} value={form.responsible_id} onChange={(e) => setForm({ ...form, responsible_id: e.target.value })}>
                <option value="">— Unassigned —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as ObjectiveStatus })}>
                {(Object.entries(OBJECTIVE_STATUS_LABEL) as [ObjectiveStatus, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function WhsObjectives() {
  return <WhsGate><ObjectivesInner /></WhsGate>;
}
